import React, { useEffect, useState, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import ApiBaseUrl from "../../ApiBaseUrl/ApiBaseUrl.json";
import "./Test.css";

function Test({ questions = [], candidateInfo }) {
  const [answers, setAnswers] = useState({});
  const [timer, setTimer] = useState(1800); // 30 minutes
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isSubmitting = useRef(false);
  const answersRef = useRef({});
  const testRef = useRef();

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isSubmitted]);

  const formatTime = (secs) => {
    const hours = String(Math.floor(secs / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
    const seconds = String(secs % 60).padStart(2, "0");
    return { hours, minutes, seconds };
  };

  const handleOptionChange = (qIndex, selectedKey) => {
    if (!isSubmitted) {
      setAnswers({ ...answers, [qIndex]: selectedKey });
    }
  };

  const calculateScore = () => {
    if (!Array.isArray(questions)) return 0;
    let score = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.answer) score++;
    });
    return score;
  };

  const handleSubmit = async () => {
    if (isSubmitted || isSubmitting.current) return;

    isSubmitting.current = true;
    setIsSubmitted(true);
    setTimer(0);

    const finalScore = calculateScore();

    const resultData = {
      name: candidateInfo.name,
      email: candidateInfo.email,
      phone: candidateInfo.phone,
      language: candidateInfo.language,
      level: candidateInfo.level,
      score: finalScore,
      total: questions.length,
      timestamp: new Date().toISOString(),
      answers: JSON.stringify(answersRef.current),
    };

    try {
      // Save result
      const res = await fetch(`${ApiBaseUrl.ApiBaseUrl}/api/test-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(resultData),
      });

      const data = await res.json();
      console.log("✅ Result saved:", data);

      // Generate PDF
      const testElement = testRef.current;
      const originalHeight = testElement.style.height;
      const originalOverflow = testElement.style.overflow;

      testElement.style.height = "auto";
      testElement.style.overflow = "visible";
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(testElement, {
        useCORS: true,
        scale: 2,
      });

      testElement.style.height = originalHeight;
      testElement.style.overflow = originalOverflow;

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const pdfBlob = pdf.output("blob");
      const formData = new FormData();
      formData.append("pdf", pdfBlob, `${candidateInfo.name}-assessment.pdf`);
      formData.append("email", candidateInfo.email);

      const uploadRes = await fetch(`${ApiBaseUrl.ApiBaseUrl}/api/save-pdf`, {
        method: "POST",
        body: formData,
      });

      const uploadText = await uploadRes.text();
      console.log("📄 PDF uploaded:", uploadText);
    } catch (error) {
      console.error("❌ Error submitting result:", error.message);
      alert("Failed to save test result or PDF.");
    }
  };

  const { hours, minutes, seconds } = formatTime(timer);
  const score = calculateScore();

  if (!questions || questions.length === 0) {
    return (
      <div className="test-wrapper">
        <h2>No questions available.</h2>
        <p>Please contact the administrator or try again later.</p>
      </div>
    );
  }

  return (
    <div className="test-wrapper" ref={testRef}>
      <div className="test-header">
        <h1 className="test-title">Welcome to the Assessment</h1>
        <p className="test-desc">
          Please complete the following assessment to evaluate your skills and
          qualifications for this role.
        </p>
      </div>

      <div className="test-container">
        <div className="test-main">
          {questions.map((q, index) => (
            <div className="question-block" key={index}>
              <h3 className="question-title">Question {index + 1}</h3>
              <p className="question-text">{q.question}</p>

              <div className="options-list">
                {Object.entries(q.options).map(([key, value]) => {
                  const selected = answers[index] === key;
                  const isCorrect = q.answer === key;

                  let optionClass = "option-label";
                  if (isSubmitted) {
                    if (selected && isCorrect) {
                      optionClass += " correct-option";
                    } else if (selected && !isCorrect) {
                      optionClass += " incorrect-option";
                    } else if (!selected && isCorrect) {
                      optionClass += " correct-option";
                    }
                  } else if (selected) {
                    optionClass += " selected";
                  }

                  return (
                    <label key={key} className={optionClass}>
                      <input
                        type="radio"
                        name={`question-${index}`}
                        value={key}
                        checked={selected}
                        disabled={isSubmitted}
                        onChange={() => handleOptionChange(index, key)}
                      />
                      <span className="option-icon" />
                      {value}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="test-sidebar">
          <div className="card applicant-card">
            <h4>Applicant Details</h4>
            <table className="invisible-table">
              <tbody>
                <tr>
                  <td>Name</td>
                  <td>{candidateInfo?.name}</td>
                </tr>
                <tr>
                  <td>Skill</td>
                  <td>{candidateInfo?.language}</td>
                </tr>
                <tr>
                  <td>Proficiency</td>
                  <td>{candidateInfo?.level}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card timer-card">
            <h4>Time Left</h4>
            <div className="timer-display">
              <div className="timer-box">
                <span>{hours}</span>
                <div className="timer-label">Hours</div>
              </div>
              <div className="timer-box">
                <span>{minutes}</span>
                <div className="timer-label">Minutes</div>
              </div>
              <div className="timer-box">
                <span>{seconds}</span>
                <div className="timer-label">Seconds</div>
              </div>
            </div>
          </div>

          {!isSubmitted && (
            <div className="card submit-card">
              <button className="submit-btn" onClick={handleSubmit}>
                Submit Test
              </button>
            </div>
          )}

          {isSubmitted && (
            <div className="card score-card">
              <h4>Your Score</h4>
              <p>
                {score} / {questions.length}
              </p>
              <div className="circular-progress-container">
                <svg width="100" height="100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="#ddd"
                    strokeWidth="10"
                    fill="none"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="#9333EA"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray="283"
                    strokeDashoffset={(1 - score / questions.length) * 283}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="progress-label">
                  {Math.round((score / questions.length) * 100)}%
                </div>
              </div>
              <p>Well done, {candidateInfo?.name}!</p>
              <p>You’ve successfully completed the assessment.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Test;